/**
 * Project state management with Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Project,
  FramedArtwork,
  ProjectFrame,
  ProjectMat,
  ProjectGlass,
  ProjectPaper,
  ProjectEffect,
  ProjectScene,
  ProjectGalleryWall,
  RestorationConfig,
  ColorGrading,
  ImageTransform,
} from '@frameit/types';

/** Editor mode */
export type EditorMode =
  | 'frame'
  | 'mat'
  | 'image'
  | 'paper'
  | 'effects'
  | 'scene'
  | 'gallery'
  | 'export';

/** Local image storage (maps artworkId to blob URL) */
const localImageStore = new Map<string, string>();

/** Remote image URLs (maps artworkId to API URL) — populated by upload hook */
const remoteImageStore = new Map<string, string>();

/** Get image URL for an artwork — local blob first, then remote API URL */
export function getLocalImageUrl(artworkId: string): string | undefined {
  return localImageStore.get(artworkId) ?? remoteImageStore.get(artworkId);
}

/** Set a remote image URL for an artwork (called after API upload) */
export function setRemoteImageUrl(artworkId: string, url: string): void {
  remoteImageStore.set(artworkId, url);
}

/** Store local image */
export function storeLocalImage(artworkId: string, file: File): string {
  // Revoke old URL if exists
  const oldUrl = localImageStore.get(artworkId);
  if (oldUrl) URL.revokeObjectURL(oldUrl);

  // Create new blob URL
  const url = URL.createObjectURL(file);
  localImageStore.set(artworkId, url);
  return url;
}

/** Project state */
interface ProjectState {
  // Current project
  project: Project | null;
  isDirty: boolean;
  isLoading: boolean;
  error: string | null;

  // Editor state
  selectedArtworkId: string | null;
  editorMode: EditorMode;
  previewQuality: 'low' | 'medium' | 'high';
  showGrid: boolean;
  snapToGrid: boolean;
  sceneZoom: number;
  canvasZoom: number;

  // Comparison mode
  showComparison: boolean;

  // Mobile UI state
  mobilePanelOpen: boolean;
  mobileArtworksOpen: boolean;

  // History for undo/redo
  history: Project[];
  historyIndex: number;

  // Actions
  loadProject: (project: Project) => void;
  createProject: (name: string) => void;
  saveProject: () => Promise<void>;
  clearProject: () => void;

  // Artwork actions
  addArtwork: (artworkId: string, frame: ProjectFrame) => void;
  removeArtwork: (framedArtworkId: string) => void;
  selectArtwork: (framedArtworkId: string | null) => void;
  updateFrame: (framedArtworkId: string, frame: Partial<ProjectFrame>) => void;
  updateMat: (framedArtworkId: string, mat: Partial<ProjectMat> | null) => void;
  updateGlass: (framedArtworkId: string, glass: Partial<ProjectGlass> | null) => void;
  updatePaper: (framedArtworkId: string, paper: Partial<ProjectPaper> | null) => void;
  addEffect: (framedArtworkId: string, effect: ProjectEffect) => void;
  removeEffect: (framedArtworkId: string, effectIndex: number) => void;
  updateEffect: (
    framedArtworkId: string,
    effectIndex: number,
    effect: Partial<ProjectEffect>,
  ) => void;
  updateRestoration: (framedArtworkId: string, config: Partial<RestorationConfig>) => void;
  updateColorGrading: (framedArtworkId: string, grading: Partial<ColorGrading>) => void;
  updateDisplayScale: (framedArtworkId: string, scale: number) => void;
  updateDisplayScaleXY: (framedArtworkId: string, scaleX: number, scaleY: number) => void;
  updateShadow: (
    framedArtworkId: string,
    shadow: Partial<NonNullable<FramedArtwork['shadow']>>,
  ) => void;
  updateImageTransform: (framedArtworkId: string, transform: Partial<ImageTransform>) => void;

  // Scene actions
  setScene: (scene: ProjectScene | undefined) => void;
  updateScenePlacement: (
    framedArtworkId: string,
    placement: Partial<ProjectScene['placements'][0]>,
  ) => void;

  // Gallery wall actions
  setGalleryWall: (galleryWall: ProjectGalleryWall | undefined) => void;
  updateGalleryPosition: (framedArtworkId: string, position: number) => void;

  // Editor actions
  setEditorMode: (mode: EditorMode) => void;
  setPreviewQuality: (quality: 'low' | 'medium' | 'high') => void;
  toggleComparison: () => void;
  toggleGrid: () => void;
  toggleSnapToGrid: () => void;
  setSceneZoom: (zoom: number) => void;
  setCanvasZoom: (zoom: number) => void;
  zoomToFit: () => void;
  setError: (error: string | null) => void;

  // Mobile UI actions
  setMobilePanelOpen: (open: boolean) => void;
  setMobileArtworksOpen: (open: boolean) => void;

  // History actions
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  saveToHistory: () => void;
}

/** Generate unique ID */
function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

/** Create project store */
export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      // Initial state
      project: null,
      isDirty: false,
      isLoading: false,
      error: null,
      selectedArtworkId: null,
      editorMode: 'frame',
      previewQuality: 'medium',
      showGrid: false,
      snapToGrid: true,
      sceneZoom: 100,
      canvasZoom: 100,
      showComparison: false,
      mobilePanelOpen: false,
      mobileArtworksOpen: false,
      history: [],
      historyIndex: -1,

      // Project actions
      loadProject: (project) => {
        set({
          project,
          isDirty: false,
          isLoading: false,
          error: null,
          selectedArtworkId: project.framedArtworks[0]?.id || null,
        });
      },

      createProject: (name) => {
        const project: Project = {
          id: `prj_${generateId()}`,
          userId: '', // Will be set on save
          name,
          framedArtworks: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set({ project, isDirty: true, selectedArtworkId: null });
      },

      saveProject: async () => {
        const { project } = get();
        if (!project) return;

        const isDevMode = !import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
        if (isDevMode) {
          // Dev mode — just mark as saved (localStorage persist handles it)
          set({ isDirty: false });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const { updateProject } = await import('../lib/api-client');
          await updateProject(project.id, {
            name: project.name,
            data: {
              name: project.name,
              framedArtworks: project.framedArtworks,
            },
          });
          set({ isDirty: false, isLoading: false });
        } catch (e) {
          set({ error: (e as Error).message, isLoading: false });
        }
      },

      clearProject: () => {
        set({
          project: null,
          isDirty: false,
          selectedArtworkId: null,
          editorMode: 'frame',
        });
      },

      // Artwork actions
      addArtwork: (artworkId, frame) => {
        const { project } = get();
        if (!project) return;

        const framedArtwork: FramedArtwork = {
          id: `fa_${generateId()}`,
          artworkId,
          frame,
          effects: [],
        };

        set({
          project: {
            ...project,
            framedArtworks: [framedArtwork, ...project.framedArtworks],
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
          selectedArtworkId: framedArtwork.id,
        });
      },

      removeArtwork: (framedArtworkId) => {
        const { project, selectedArtworkId } = get();
        if (!project) return;

        const newArtworks = project.framedArtworks.filter((a) => a.id !== framedArtworkId);

        set({
          project: {
            ...project,
            framedArtworks: newArtworks,
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
          selectedArtworkId:
            selectedArtworkId === framedArtworkId ? newArtworks[0]?.id || null : selectedArtworkId,
        });
      },

      selectArtwork: (framedArtworkId) => {
        set({ selectedArtworkId: framedArtworkId });
      },

      updateFrame: (framedArtworkId, frame) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId ? { ...a, frame: { ...a.frame, ...frame } } : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateMat: (framedArtworkId, mat) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId
                ? { ...a, mat: mat ? ({ ...a.mat, ...mat } as ProjectMat) : undefined }
                : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateGlass: (framedArtworkId, glass) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId
                ? { ...a, glass: glass ? ({ ...a.glass, ...glass } as ProjectGlass) : undefined }
                : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updatePaper: (framedArtworkId, paper) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId
                ? { ...a, paper: paper ? ({ ...a.paper, ...paper } as ProjectPaper) : undefined }
                : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      addEffect: (framedArtworkId, effect) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId ? { ...a, effects: [...a.effects, effect] } : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      removeEffect: (framedArtworkId, effectIndex) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId
                ? { ...a, effects: a.effects.filter((_, i) => i !== effectIndex) }
                : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateEffect: (framedArtworkId, effectIndex, effect) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId
                ? {
                    ...a,
                    effects: a.effects.map((e, i) => (i === effectIndex ? { ...e, ...effect } : e)),
                  }
                : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateRestoration: (framedArtworkId, config) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId ? { ...a, restoration: { ...a.restoration, ...config } } : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateColorGrading: (framedArtworkId, grading) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId
                ? { ...a, colorGrading: { ...a.colorGrading, ...grading } }
                : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateDisplayScale: (framedArtworkId, scale) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId ? { ...a, displayScale: scale } : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateDisplayScaleXY: (framedArtworkId, scaleX, scaleY) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId ? { ...a, displayScaleX: scaleX, displayScaleY: scaleY } : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateShadow: (framedArtworkId, shadow) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId
                ? {
                    ...a,
                    shadow: {
                      enabled: a.shadow?.enabled ?? true,
                      offsetX: a.shadow?.offsetX ?? 5,
                      offsetY: a.shadow?.offsetY ?? 5,
                      blur: a.shadow?.blur ?? 15,
                      color: a.shadow?.color ?? '#000000',
                      opacity: a.shadow?.opacity ?? 0.3,
                      ...shadow,
                    },
                  }
                : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateImageTransform: (framedArtworkId, transform) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            framedArtworks: project.framedArtworks.map((a) =>
              a.id === framedArtworkId
                ? {
                    ...a,
                    imageTransform: {
                      rotation: a.imageTransform?.rotation ?? 0,
                      flipH: a.imageTransform?.flipH ?? false,
                      flipV: a.imageTransform?.flipV ?? false,
                      ...a.imageTransform,
                      ...transform,
                    },
                  }
                : a,
            ),
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      // Scene actions
      setScene: (scene) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            scene,
            galleryWall: scene ? undefined : project.galleryWall,
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateScenePlacement: (framedArtworkId, placement) => {
        const { project } = get();
        if (!project?.scene) return;

        set({
          project: {
            ...project,
            scene: {
              ...project.scene,
              placements: project.scene.placements.map((p) =>
                p.framedArtworkId === framedArtworkId ? { ...p, ...placement } : p,
              ),
            },
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      // Gallery wall actions
      setGalleryWall: (galleryWall) => {
        const { project } = get();
        if (!project) return;

        set({
          project: {
            ...project,
            galleryWall,
            scene: galleryWall ? undefined : project.scene,
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      updateGalleryPosition: (framedArtworkId, position) => {
        const { project } = get();
        if (!project?.galleryWall) return;

        set({
          project: {
            ...project,
            galleryWall: {
              ...project.galleryWall,
              positions: project.galleryWall.positions.map((p) =>
                p.framedArtworkId === framedArtworkId ? { ...p, position } : p,
              ),
            },
            updatedAt: new Date().toISOString(),
          },
          isDirty: true,
        });
      },

      // Editor actions
      setEditorMode: (mode) => set({ editorMode: mode }),
      setPreviewQuality: (quality) => set({ previewQuality: quality }),
      toggleComparison: () => set((s) => ({ showComparison: !s.showComparison })),
      toggleGrid: () => set((s) => ({ showGrid: !s.showGrid })),
      toggleSnapToGrid: () => set((s) => ({ snapToGrid: !s.snapToGrid })),
      setSceneZoom: (zoom) => set({ sceneZoom: zoom }),
      setCanvasZoom: (zoom) => set({ canvasZoom: zoom }),
      zoomToFit: () => set({ canvasZoom: 100, sceneZoom: 100 }),
      setError: (error) => set({ error }),

      // Mobile UI actions
      setMobilePanelOpen: (open) => set({ mobilePanelOpen: open }),
      setMobileArtworksOpen: (open) => set({ mobileArtworksOpen: open }),

      // History actions
      saveToHistory: () => {
        const { project, history, historyIndex } = get();
        if (!project) return;

        // Remove any future history if we're not at the end
        const newHistory = history.slice(0, historyIndex + 1);
        // Add current state to history (deep clone)
        newHistory.push(JSON.parse(JSON.stringify(project)));
        // Limit history to 50 items
        if (newHistory.length > 50) newHistory.shift();

        set({
          history: newHistory,
          historyIndex: newHistory.length - 1,
        });
      },

      undo: () => {
        const { history, historyIndex } = get();
        if (historyIndex <= 0) return;

        const newIndex = historyIndex - 1;
        const previousState = history[newIndex];

        set({
          project: JSON.parse(JSON.stringify(previousState)),
          historyIndex: newIndex,
          isDirty: true,
        });
      },

      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex >= history.length - 1) return;

        const newIndex = historyIndex + 1;
        const nextState = history[newIndex];

        set({
          project: JSON.parse(JSON.stringify(nextState)),
          historyIndex: newIndex,
          isDirty: true,
        });
      },

      canUndo: () => {
        const { historyIndex } = get();
        return historyIndex > 0;
      },

      canRedo: () => {
        const { history, historyIndex } = get();
        return historyIndex < history.length - 1;
      },
    }),
    {
      name: 'frameit-project',
      partialize: (state) => ({
        project: state.project,
        previewQuality: state.previewQuality,
        showGrid: state.showGrid,
        snapToGrid: state.snapToGrid,
      }),
    },
  ),
);

/** Get selected artwork */
export function useSelectedArtwork() {
  return useProjectStore((state) => {
    if (!state.project || !state.selectedArtworkId) return null;
    return state.project.framedArtworks.find((a) => a.id === state.selectedArtworkId);
  });
}
